import React, {
    ChangeEvent,
    FocusEvent,
    InputHTMLAttributes,
    SelectHTMLAttributes,
} from "react";

import {
    FieldErrors,
    FieldValues,
    Path,
    RegisterOptions,
    UseFormRegister,
    UseFormSetValue,
    get,
} from "react-hook-form";

import "./AppInput_New.scss";

/*
 * ---------------------------------------------------------
 * Select option
 * ---------------------------------------------------------
 */
export interface AppInput_NewOption {
    label: string;
    value: string;
    disabled?: boolean;
}

/*
 * ---------------------------------------------------------
 * Common props
 * ---------------------------------------------------------
 */
interface AppInput_NewCommonProps<
    TFieldValues extends FieldValues = FieldValues
> {
    name: Path<TFieldValues>;

    label?: string;

    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;

    id?: string;

    hint?: string;
    error?: string;

    validation?: RegisterOptions<TFieldValues>;

    register?: UseFormRegister<TFieldValues>;

    errors?: FieldErrors<TFieldValues>;

    setValue?: UseFormSetValue<TFieldValues>;

    className?: string;
}

/*
 * ---------------------------------------------------------
 * Input props
 *
 * We explicitly omit handlers that the component controls
 * itself. This prevents React's HTML handler types from
 * conflicting with the select version.
 * ---------------------------------------------------------
 */
export interface AppInput_NewInputProps<
    TFieldValues extends FieldValues = FieldValues
> extends AppInput_NewCommonProps<TFieldValues>,
        Omit<
            InputHTMLAttributes<HTMLInputElement>,
            | "name"
            | "value"
            | "onChange"
            | "onBlur"
            | "onKeyDown"
        > {
    fieldType?: "input";

    value?: string | number | readonly string[];

    type?: string;

    placeholder?: string;

    maxLength?: number;
    minLength?: number;

    min?: number | string;
    max?: number | string;

    autoComplete?: string;
    autoFocus?: boolean;

    onCustomChange?: (
        event: ChangeEvent<HTMLInputElement>
    ) => void;

    onCustomBlur?: (
        event: FocusEvent<HTMLInputElement>
    ) => void;

    onKeyDown?: (
        event: React.KeyboardEvent<HTMLInputElement>
    ) => void;
}

/*
 * ---------------------------------------------------------
 * Select props
 *
 * We omit the handlers controlled by this component.
 * ---------------------------------------------------------
 */
export interface AppInput_NewSelectProps<
    TFieldValues extends FieldValues = FieldValues
> extends AppInput_NewCommonProps<TFieldValues>,
        Omit<
            SelectHTMLAttributes<HTMLSelectElement>,
            | "name"
            | "value"
            | "onChange"
            | "onBlur"
        > {
    fieldType: "select";

    value?: string;

    options: AppInput_NewOption[];

    placeholder?: string;

    onCustomChange?: (
        value: string
    ) => void;

    onCustomBlur?: (
        event: FocusEvent<HTMLSelectElement>
    ) => void;
}

/*
 * ---------------------------------------------------------
 * Combined props
 * ---------------------------------------------------------
 */
export type AppInput_NewProps<
    TFieldValues extends FieldValues = FieldValues
> =
    | AppInput_NewInputProps<TFieldValues>
    | AppInput_NewSelectProps<TFieldValues>;

/*
 * ---------------------------------------------------------
 * Component
 * ---------------------------------------------------------
 */
function AppInput_New<
    TFieldValues extends FieldValues = FieldValues
>(
    props: AppInput_NewProps<TFieldValues>
) {
    const {
        name,
        label,

        required = false,
        disabled = false,

        id,

        hint,
        error,

        validation,

        register,
        errors,

        setValue,

        className = "",
    } = props;

    const fieldId = id || String(name);

    /*
     * -------------------------------------------------------
     * React Hook Form registration
     * -------------------------------------------------------
     */
    const registration = register
        ? register(name, {
              ...validation,
              required:
                  validation?.required ??
                  (required
                      ? `${label || String(name)} is required`
                      : undefined),
          })
        : undefined;

    /*
     * -------------------------------------------------------
     * RHF error
     * -------------------------------------------------------
     */
    const rhfError = errors
        ? get(errors, String(name))
        : undefined;

    const rhfErrorMessage =
        typeof rhfError?.message === "string"
            ? rhfError.message
            : undefined;

    /*
     * Explicit error takes priority.
     */
    const displayError =
        error || rhfErrorMessage;

    const hasError = Boolean(displayError);

    /*
     * -------------------------------------------------------
     * Accessibility
     * -------------------------------------------------------
     */
    const describedBy = [
        hint && !hasError
            ? `${fieldId}-hint`
            : "",
        hasError
            ? `${fieldId}-error`
            : "",
    ]
        .filter(Boolean)
        .join(" ") || undefined;

    /*
     * -------------------------------------------------------
     * Input change
     *
     * This function is only used when fieldType !== "select".
     * Because props is narrowed before calling this function,
     * onCustomChange is correctly typed as an input event.
     * -------------------------------------------------------
     */
    const handleInputChange = (
        event: ChangeEvent<HTMLInputElement>
    ) => {
        if (registration) {
            registration.onChange(event);
        } else if (setValue) {
            setValue(
                name,
                event.target.value as never,
                {
                    shouldDirty: true,
                    shouldValidate: true,
                }
            );
        }

        if (
            props.fieldType !== "select" &&
            props.onCustomChange
        ) {
            props.onCustomChange(event);
        }
    };

    /*
     * -------------------------------------------------------
     * Select change
     *
     * Select custom change intentionally receives only the
     * selected string value.
     * -------------------------------------------------------
     */
    const handleSelectChange = (
        event: ChangeEvent<HTMLSelectElement>
    ) => {
        if (registration) {
            registration.onChange(event);
        } else if (setValue) {
            setValue(
                name,
                event.target.value as never,
                {
                    shouldDirty: true,
                    shouldValidate: true,
                }
            );
        }

        if (
            props.fieldType === "select" &&
            props.onCustomChange
        ) {
            props.onCustomChange(
                event.target.value
            );
        }
    };

    /*
     * -------------------------------------------------------
     * Input blur
     * -------------------------------------------------------
     */
    const handleInputBlur = (
        event: FocusEvent<HTMLInputElement>
    ) => {
        registration?.onBlur(event);

        if (
            props.fieldType !== "select" &&
            props.onCustomBlur
        ) {
            props.onCustomBlur(event);
        }
    };

    /*
     * -------------------------------------------------------
     * Select blur
     * -------------------------------------------------------
     */
    const handleSelectBlur = (
        event: FocusEvent<HTMLSelectElement>
    ) => {
        registration?.onBlur(event);

        if (
            props.fieldType === "select" &&
            props.onCustomBlur
        ) {
            props.onCustomBlur(event);
        }
    };

    /*
     * -------------------------------------------------------
     * Input key down
     * -------------------------------------------------------
     */
    const handleInputKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement>
    ) => {
        if (event.key === "Enter") {
            event.preventDefault();
        }

        if (
            props.fieldType !== "select" &&
            props.onKeyDown
        ) {
            props.onKeyDown(event);
        }
    };

    /*
     * -------------------------------------------------------
     * Wrapper
     * -------------------------------------------------------
     */
    return (
        <div
            className={[
                "app-input-new-wrapper",
                hasError ? "has-error" : "",
                disabled ? "is-disabled" : "",
                className,
            ]
                .filter(Boolean)
                .join(" ")}
        >
            {label && (
                <label
                    htmlFor={fieldId}
                    className="app-input-new-label"
                >
                    <span>{label}</span>

                    {required && (
                        <span
                            className="app-input-new-required"
                            aria-hidden="true"
                        >
                            *
                        </span>
                    )}
                </label>
            )}

            {/*
             * =================================================
             * SELECT
             * =================================================
             */}
            {props.fieldType === "select" ? (
                <div className="app-input-new-select-container">
                    <select
                        {...props}
                        {...registration}
                        id={fieldId}
                        name={String(name)}
                        value={props.value ?? ""}
                        disabled={disabled}
                        aria-invalid={hasError}
                        aria-describedby={describedBy}
                        className="app-input-new app-input-new-select"
                        onChange={handleSelectChange}
                        onBlur={handleSelectBlur}
                    >
                        {props.placeholder && (
                            <option
                                value=""
                                disabled
                            >
                                {props.placeholder}
                            </option>
                        )}

                        {props.options.map(
                            (option) => (
                                <option
                                    key={option.value}
                                    value={option.value}
                                    disabled={
                                        option.disabled
                                    }
                                >
                                    {option.label}
                                </option>
                            )
                        )}
                    </select>

                    <span
                        className="app-input-new-select-arrow"
                        aria-hidden="true"
                    >
                        <span />
                    </span>
                </div>
            ) : (
                /*
                 * =================================================
                 * INPUT
                 * =================================================
                 */
                <input
                    {...props}
                    {...registration}
                    id={fieldId}
                    name={String(name)}
                    type={props.type || "text"}
                    value={props.value ?? ""}
                    placeholder={props.placeholder}
                    disabled={disabled}
                    readOnly={props.readOnly}
                    required={required}
                    maxLength={props.maxLength}
                    minLength={props.minLength}
                    min={props.min}
                    max={props.max}
                    autoComplete={props.autoComplete}
                    autoFocus={props.autoFocus}
                    aria-invalid={hasError}
                    aria-describedby={describedBy}
                    className="app-input-new"
                    onChange={handleInputChange}
                    onBlur={handleInputBlur}
                    onKeyDown={handleInputKeyDown}
                />
            )}

             {/*
             * -------------------------------------------------
             * Hint
             * -------------------------------------------------
             */}
            {hint && !hasError && (
                <div
                    id={`${fieldId}-hint`}
                    className="app-input-new-hint"
                >
                    {hint}
                </div>
            )}

             {/*
             * -------------------------------------------------
             * Hint
             * -------------------------------------------------
             */}
            {hasError && (
                <div
                    id={`${fieldId}-error`}
                    className="app-input-new-error"
                    role="alert"
                >
                    <span
                        className="app-input-new-error-icon"
                        aria-hidden="true"
                    >
                        !
                    </span>

                    <span>
                        {displayError}
                    </span>
                </div>
            )}
        </div>
    );
}

export default AppInput_New;